import { Injectable } from '@angular/core'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Observable, of, from } from 'rxjs'
import { map, catchError } from 'rxjs/operators'
import { HelpersService } from '../../helpers.service'
import { BuilderService } from '../../builder-services/builder.service'
import { DomainsApiService } from 'src/app/builder-services/api/domains.api.service'
import { SettingsApiService } from 'src/app/builder-services/api/settings.api.service'
import { environment } from '../../../environments/environment'

@Injectable({
  providedIn: 'root'
})
export class DomainService {
  private builderUrl: string
  private domainSettings: any = {}

  options = {
    headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded')
  }

  constructor(
    private http: HttpClient,
    private helper: HelpersService,
    private builderService: BuilderService,
    private domainsApi: DomainsApiService,
    private settingsApi: SettingsApiService,
  ) {
    this.builderUrl = this.helper.getBuilderUrl()
  }

  /**
   * Generate subdomain from project/app name
   * Sanitizes the name to be URL-safe
   */
  generateSubdomainFromName(name: string): string {
    let subdomain = name
      .toLowerCase()
      .trim()
      // Remove special characters
      .replace(/[^a-z0-9\s-]/g, '')
      // Replace spaces with hyphens
      .replace(/\s+/g, '-')
      // Remove consecutive hyphens
      .replace(/-+/g, '-')
      // Remove leading/trailing hyphens
      .replace(/^-+|-+$/g, '')
      // Truncate to max 63 chars (DNS label limit)
      .substring(0, 63)

    return subdomain || 'mystore'
  }

  /**
   * Check if subdomain is available (unique across all projects)
   * Queries stacks_domain_mappings in Supabase for O(1) performance
   */
  checkSubdomainAvailability(subdomain: string, currentProjectId?: string): Observable<any> {
    return new Observable((observer) => {
      this.domainsApi.getDomainMappingBySubdomain(subdomain).then((data: any) => {
        const isTaken = data && String(data.project_id) !== String(currentProjectId)
        if (!isTaken) {
          observer.next({ available: true, suggestion: '' })
          observer.complete()
        } else {
          this.generateSubdomainSuggestion(subdomain).subscribe(
            (suggestion) => {
              observer.next({ available: false, suggestion })
              observer.complete()
            },
            () => {
              observer.next({ available: false, suggestion: '' })
              observer.complete()
            }
          )
        }
      }).catch(() => {
        observer.next({ available: true, suggestion: '' })
        observer.complete()
      })
    })
  }

  /**
   * Generate alternative subdomain suggestion
   * Uses Supabase stacks_domain_mappings to check availability
   */
  private generateSubdomainSuggestion(subdomain: string): Observable<string> {
    return new Observable((observer) => {
      this.findAvailableSubdomain(subdomain, 2).subscribe(
        (suggestion) => {
          observer.next(suggestion)
          observer.complete()
        },
        () => {
          // Fallback to random if something fails
          observer.next(`${subdomain}-${Math.floor(Math.random() * 10000)}`)
          observer.complete()
        }
      )
    })
  }

  /**
   * Recursively find available subdomain by checking stacks_domain_mappings
   */
  private findAvailableSubdomain(baseSubdomain: string, counter: number): Observable<string> {
    const suggestion = `${baseSubdomain}-${counter}`

    // Safety limit
    if (counter > 100) {
      return of(`${baseSubdomain}-${Math.floor(Math.random() * 10000)}`)
    }

    return new Observable((observer) => {
      this.domainsApi.getDomainMappingBySubdomain(suggestion).then((data: any) => {
        if (data) {
          // Subdomain is taken, try next
          this.findAvailableSubdomain(baseSubdomain, counter + 1).subscribe(
            (result) => {
              observer.next(result)
              observer.complete()
            }
          )
        } else {
          // Subdomain is available
          observer.next(suggestion)
          observer.complete()
        }
      }).catch(() => {
        // On error, treat as available
        observer.next(suggestion)
        observer.complete()
      })
    })
  }

  /**
   * Save subdomain to Supabase project_settings and stacks_domain_mappings
   */
  saveSubdomain(projectId: string, subdomain: string): Observable<any> {
    const subdomainFull = `${subdomain}.${environment.sitesDomain}`
    const now = new Date().toISOString()

    return new Observable((observer) => {
      this.settingsApi.getSettingsByType(projectId, 'domain_settings').then((existingSettings: any) => {
        const mergedData = {
          ...existingSettings,
          subdomain,
          subdomain_full: subdomainFull,
          domain_status: 'active',
          updated_at: now,
          created_at: existingSettings?.created_at || now,
        }

        const previousKey = existingSettings?.custom_domain || existingSettings?.subdomain_full || existingSettings?.subdomain

        Promise.all([
          this.settingsApi.upsertSettingsByType(projectId, 'domain_settings', mergedData),
          this.domainsApi.upsertDomainMapping(projectId, { subdomain }),
        ])
          .then(() => {
            void this.resyncVibeSite(projectId, previousKey)
            observer.next({ success: true, subdomain_full: subdomainFull })
            observer.complete()
          })
          .catch((error: unknown) => {
            console.error('Failed to save subdomain:', error)
            observer.error(error)
          })
      }).catch((error: unknown) => {
        observer.error(error)
      })
    })
  }

  /**
   * Validate custom domain format
   */
  validateDomainFormat(domain: string): boolean {
    const domainRegex = /^(?!:\/\/)([a-zA-Z0-9-_]+\.)*[a-zA-Z0-9][a-zA-Z0-9-_]+\.[a-zA-Z]{2,11}?$/
    return domainRegex.test(domain)
  }

  /**
   * Verify custom domain DNS and reachability
   */
  verifyDomainDNS(projectId: string, domain: string): Observable<any> {
    let postData = new URLSearchParams()
    postData.set('project_id', projectId)
    postData.set('domain', domain)
    postData.set('token', this.builderService.getToken() || '')

    return this.http.post(this.builderUrl + 'verify-custom-domain', postData.toString(), this.options).pipe(
      map((response: any) => {
        return {
          verified: response.verified || false,
          reachable: response.reachable || false,
          dns_records: response.dns_records || [],
          error: response.error || null,
          message: response.message || null
        }
      }),
      catchError((error) => {
        return of({
          verified: false,
          reachable: false,
          dns_records: [],
          error: error.message || 'Failed to verify domain',
          message: null
        })
      })
    )
  }

  checkDomainStatus(projectId: string, domain: string): Observable<any> {
    let postData = new URLSearchParams()
    postData.set('project_id', projectId)
    postData.set('domain', domain)
    postData.set('token', this.builderService.getToken() || '')

    return this.http.post(this.builderUrl + 'check-custom-domain-status', postData.toString(), this.options).pipe(
      map((response: any) => {
        return {
          verified: response.verified || false,
          reachable: response.reachable || false,
          status: response.status || 'unknown',
          dns_records: response.dns_records || [],
          error: response.error || null,
          message: response.message || null
        }
      }),
      catchError((error) => {
        return of({
          verified: false,
          reachable: false,
          status: 'error',
          dns_records: [],
          error: error.message || 'Failed to check domain status',
          message: null
        })
      })
    )
  }

  /**
   * Save custom domain to Supabase project_settings and stacks_domain_mappings
   */
  saveCustomDomain(projectId: string, domain: string, verified: boolean = false): Observable<any> {
    const now = new Date().toISOString()

    return new Observable((observer) => {
      this.settingsApi.getSettingsByType(projectId, 'domain_settings').then((_existingSettings: any) => {
        const previousKey = _existingSettings?.custom_domain || _existingSettings?.subdomain_full || _existingSettings?.subdomain
        const tasks: Promise<void>[] = [
          this.settingsApi.upsertSettingsByType(projectId, 'domain_settings', {
            custom_domain: domain,
            custom_domain_verified: verified,
            last_verified: now,
            updated_at: now,
          }),
        ]

        if (verified) {
          tasks.push(this.domainsApi.upsertDomainMapping(projectId, { custom_domain: domain }))
        }

        Promise.all(tasks)
          .then(() => {
            void this.resyncVibeSite(projectId, previousKey)
            observer.next({ success: true, message: 'Custom domain saved successfully' })
            observer.complete()
          })
          .catch((error: unknown) => {
            console.error('Failed to save custom domain:', error)
            observer.error(error)
          })
      }).catch(() => {
        observer.error(new Error('Failed to get existing domain settings'))
      })
    })
  }

  /**
   * Tell stacks-agents to move an AI-designed site to the folder its new domain resolves to.
   *
   * Those pages are served from `projects/<host>/`, so a domain change leaves them in the old
   * folder and the new host serving nothing. The backend works out both folder names itself;
   * `previousKey` is only a fallback for sites deployed before it started recording them.
   *
   * Never blocks saving the domain — a project with no AI pages is a no-op server-side.
   */
  private async resyncVibeSite(projectId: string, previousKey?: string): Promise<void> {
    try {
      await this.domainsApi.resyncVibeSite(projectId, previousKey)
    } catch (error) {
      console.warn('Domain saved, but the AI-designed site could not be moved to it:', error)
    }
  }

  /**
   * Get domain settings from Supabase project_settings
   */
  getDomainSettings(projectId: string): Observable<any> {
    return from(this.settingsApi.getSettingsByType(projectId, 'domain_settings')).pipe(
      map((settings: any) => {
        this.domainSettings = settings || {}
        return this.domainSettings
      }),
      catchError((error) => {
        console.error('Error fetching domain settings:', error)
        return of({})
      })
    )
  }

  /**
   * Check if user can access custom domain feature (premium with website package)
   */
  canAccessCustomDomain(): boolean {
    return this.builderService.is_premium
  }

  /**
   * Get full subdomain URL
   */
  getFullSubdomainUrl(subdomain: string): string {
    return `https://${subdomain}.${environment.sitesDomain}`
  }

  /**
   * Get website URL with priority: custom domain > subdomain > fallback
   */
  getWebsiteUrl(projectId: string, env: 'prod' | 'staging' = 'prod'): string {
    const domainSettings = this.domainSettings

    // Priority 1: Custom domain (if verified and premium)
    if (domainSettings.custom_domain && domainSettings.custom_domain_verified && this.canAccessCustomDomain()) {
      return `https://${domainSettings.custom_domain}`
    }

    // Priority 2: Subdomain
    if (domainSettings.subdomain_full) {
      return `https://${domainSettings.subdomain_full}`
    }

    // Fallback: Default renderer with project ID
    return `https://${environment.sitesDomain}?project_id=${projectId}`
  }
}
